/**
 * Thin client helpers for the admin-side `/api/admin/requests/*` endpoints.
 *
 * Admin session is carried via the `admin_session` httpOnly cookie, so every
 * call uses `credentials: "include"`. No tokens are ever read or stored by
 * the frontend.
 */

// API calls use relative `/api/...` paths. See ContentProvider.jsx.

async function _json(res) {
  let data = null;
  try { data = await res.json(); } catch { /* ignore */ }
  if (!res.ok) {
    const err = new Error((data && (data.detail || data.message)) || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function fetchSummary() {
  const res = await fetch(`/api/admin/requests/summary`, {
    credentials: "include",
  });
  return _json(res);
}

export async function fetchRequests({ type = "", statusFilter = "", limit = 50, skip = 0 } = {}) {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (statusFilter) params.set("status_filter", statusFilter);
  if (limit) params.set("limit", String(limit));
  if (skip) params.set("skip", String(skip));
  const qs = params.toString();
  const res = await fetch(
    `/api/admin/requests${qs ? `?${qs}` : ""}`,
    { credentials: "include" }
  );
  return _json(res);
}

export async function fetchRequestDetail(id) {
  const res = await fetch(`/api/admin/requests/${id}`, {
    credentials: "include",
  });
  return _json(res);
}

export async function patchRequestStatus(id, status) {
  const res = await fetch(`/api/admin/requests/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
  return _json(res);
}

/**
 * Build the CSV export URL for the current admin filters.
 *
 * Returned as a URL (not a fetch result) so the caller can render it on a
 * normal anchor tag — letting the browser handle the file download with
 * the cookie auto-attached, instead of buffering the whole file in JS.
 */
export function buildRequestsCsvExportUrl({ type = "", statusFilter = "" } = {}) {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (statusFilter) params.set("status_filter", statusFilter);
  const qs = params.toString();
  return `/api/admin/requests/export.csv${qs ? `?${qs}` : ""}`;
}
